import { createContext, useContext, useState, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DeleteConfirmContext = createContext(null);

export function DeleteConfirmProvider({ children }) {
  const [dialog, setDialog] = useState({
    open: false,
    title: "Confirm Delete",
    message: "",
    onConfirm: null,
    actionLabel: "Delete",
    variant: "destructive" // "destructive" for delete, "warning" for restore/archive
  });

  const showConfirm = useCallback((options) => {
    setDialog({
      open: true,
      title: options.title || "Confirm Delete",
      message: options.message || "Are you sure?",
      onConfirm: options.onConfirm,
      actionLabel: options.actionLabel || "Delete",
      variant: options.variant || "destructive"
    });
  }, []);

  const closeDialog = useCallback(() => {
    setDialog(prev => ({ ...prev, open: false, onConfirm: null }));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleConfirm = useCallback(async () => {
    if (dialog.onConfirm) {
      await dialog.onConfirm();
    }
    closeDialog();
  }, [dialog.onConfirm, closeDialog]);

  return (
    <DeleteConfirmContext.Provider value={{ showConfirm, closeDialog }}>
      {children}
      <AlertDialog open={dialog.open} onOpenChange={(open) => !open && closeDialog()}>
        <AlertDialogContent className="bg-zinc-900 border border-zinc-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{dialog.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400 whitespace-pre-line">
              {dialog.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirm}
              className={dialog.variant === "destructive" 
                ? "bg-red-600 text-white hover:bg-red-700" 
                : "bg-amber-600 text-white hover:bg-amber-700"
              }
            >
              {dialog.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DeleteConfirmContext.Provider>
  );
}

export function useDeleteConfirm() {
  const context = useContext(DeleteConfirmContext);
  if (!context) {
    throw new Error("useDeleteConfirm must be used within a DeleteConfirmProvider");
  }
  return context;
}

// Helper function that returns a promise-based confirm dialog
// This mimics window.confirm() but with a centered dialog
export function useConfirmDialog() {
  const { showConfirm, closeDialog } = useDeleteConfirm();
  
  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      showConfirm({
        ...options,
        onConfirm: () => {
          resolve(true);
        }
      });
      // Handle cancel - when dialog closes without confirming
      // This is tricky - we need to resolve false on cancel
    });
  }, [showConfirm]);

  return { confirm };
}
